import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
} from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';
import { ApiErrorResponse, ValidationError } from '../models/api-error.model';
import { AuthService } from '../services/auth.service';

export const apiErrorInterceptor =
  (): HttpInterceptorFn =>
  (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> => {
    const router = inject(Router);
    const notificationService = inject(NotificationService);
    const authService = inject(AuthService);

    return next(req).pipe(
      catchError((errorResponse) => {
        // Skip error handling if explicitly requested (e.g., logout)
        if (req.headers.get('skipErrorHandling') === 'true') {
          return throwError(() => errorResponse);
        }
        
        if (errorResponse) {
          switch (errorResponse.status) {
            case 400:
            case 409:
              {
                const apiError: ApiErrorResponse = errorResponse.error;
                const errors = apiError?.errors;

                if (errors && Array.isArray(errors) && errors.length > 0) {
                  // Handle array of validation errors
                  const errorMessages: string[] = errors
                    .map((err: ValidationError) => err.description)
                    .filter((msg): msg is string => !!msg);

                  if (errorMessages.length > 0) {
                    notificationService.showError(errorMessages);
                  } else {
                    notificationService.showError(
                      apiError?.detail || 'An unknown error occurred!',
                    );
                  }
                } else if (apiError?.detail) {
                  // Handle single detail message
                  notificationService.showError(apiError.detail);
                } else {
                  notificationService.showError('An unknown error occurred!');
                }
              }
              break;
            case 401:
              {
                const skipAuth = req.headers.get('skipAuth') === 'true';
                const isLogoutRequest = req.url.includes('/auth/logout') || req.url.includes('/logout');

                if (!skipAuth && !isLogoutRequest) {
                  authService.logout();
                }
              }
              break;
            case 403:
              router.navigate(['/error/403']);
              break;
            case 404:
              router.navigate(['/error/404']);
              break;
            case 500:
              router.navigate(['/error/500']);
              break;
          }
        } else {
          router.navigate(['/error/500']);
        }

        return throwError(() => errorResponse);
      }),
    );
  };
